/**
 * Запросы AI-ассистента (US-10) к `/api/assistant/*`.
 *
 * Здесь и только здесь сырой контракт бэка (`Api*` из `types/assistant`)
 * приводится к виду, удобному компонентам. Когда бэк меняет форму ответа,
 * правится этот файл — компоненты не трогаются.
 *
 * Контракт: `digital-passport-v2/docs/US-10_ai_assistant_api.md`.
 */
import { api } from './api';
import type {
  ApiBlock,
  ApiChatResponse,
  ApiModesResponse,
  ApiNoteBlock,
  ApiTableBlock,
  AssistantAction,
  AssistantAnswer,
  AssistantBlock,
  AssistantMode,
  AssistantSource,
  AssistantTableBlock,
  NoteFormat,
} from '../types/assistant';

/**
 * `module_id` → страница витрины.
 *
 * Бэк отдаёт идентификатор виджета, а НЕ готовый URL, и это правильно:
 * маршруты знает фронт, и вторая их копия на бэке разъехалась бы при первом
 * переименовании раздела. Плата — эта карта.
 *
 * Якорь `#<module_id>` добавляется всегда: подсветка конкретного виджета
 * (FR-4.2) в объём US-10 не входит, но как только у виджетов появятся `id`,
 * переход станет точным без правок ассистента.
 */
const moduleHref = (moduleId: string, kato: number | null): string | null => {
  const [section] = moduleId.split('.');
  const anchor = `#${moduleId}`;
  switch (section) {
    case 'analytics':
      return `/analytics${anchor}`;
    case 'projects':
      return `/projects${anchor}`;
    case 'left-menu':
    case 'home':
    case 'digital-passport':
      // Паспорт territory-scoped: без КАТО вести некуда, и «куда-нибудь»
      // хуже отсутствия кнопки.
      return kato === null ? null : `/${kato}/digital-passport${anchor}`;
    default:
      return null;
  }
};

/** Колонка таблицы: числовая, если все непустые ячейки начинаются с цифры. */
const toColumns = (
  block: ApiTableBlock,
): AssistantTableBlock['columns'] =>
  block.columns.map((label, index) => ({
    key: `c${index}`,
    label,
    numeric: block.rows.every((row) => {
      const cell = row[index];
      return cell === undefined || cell === '' || /^[-+]?[\d\s.,]/.test(cell);
    }),
  }));

const toTable = (block: ApiTableBlock): AssistantTableBlock => ({
  kind: 'table',
  title: block.title || null,
  subtitle: block.subtitle,
  columns: toColumns(block),
  rows: block.rows.map((row) =>
    Object.fromEntries(row.map((cell, index) => [`c${index}`, cell])),
  ),
});

const toNote = (block: ApiNoteBlock): AssistantBlock => ({
  kind: 'note',
  noteId: block.note_id,
  heading: block.title,
  subtitle: block.subtitle,
  footer: block.footer,
  // Пустой раздел не показываем: заголовок «Рекомендации» без текста в
  // документе на подпись выглядит недоделанной работой.
  sections: block.sections
    .filter((section) => section.body.trim() !== '')
    .map((section) => ({ heading: section.heading, text: section.body })),
  tables: block.tables.map(toTable),
  formats: block.formats,
});

/** Разложить блоки бэка на карточки, текст, источники и кнопки. */
const normalizeBlocks = (
  blocks: ApiBlock[],
  kato: number | null,
): Pick<AssistantAnswer, 'text' | 'blocks' | 'sources' | 'actions'> => {
  const texts: string[] = [];
  const cards: AssistantBlock[] = [];
  const sources: AssistantSource[] = [];
  const actions: AssistantAction[] = [];
  let noteId: string | null = null;

  for (const block of blocks) {
    switch (block.type) {
      case 'text':
        if (block.text.trim() !== '') texts.push(block.text);
        break;
      case 'bars':
        cards.push({
          kind: 'bars',
          title: block.title || null,
          subtitle: block.subtitle,
          unit: block.unit,
          rows: block.rows.map(({ label, value }) => ({ label, value })),
        });
        break;
      case 'line':
        cards.push({
          kind: 'series',
          title: block.title || null,
          subtitle: block.subtitle,
          unit: block.unit,
          points: block.points,
        });
        break;
      case 'table':
        cards.push(toTable(block));
        break;
      case 'note': {
        const note = toNote(block);
        cards.push(note);
        noteId = block.note_id;
        // Кнопки выгрузки строятся из `formats` записки, а не из отдельного
        // действия: бэк перечисляет форматы там.
        for (const format of block.formats) {
          actions.push({
            kind: 'export',
            label: format.toUpperCase(),
            format,
            noteId: block.note_id,
          });
        }
        break;
      }
      case 'source':
        sources.push({
          title: block.title,
          detail: block.detail,
          period: block.period,
          href:
            block.module_id === null ? null : moduleHref(block.module_id, kato),
        });
        break;
      case 'actions':
        for (const action of block.actions) {
          if (action.kind === 'open_widget' && action.module_id !== null) {
            const href = moduleHref(action.module_id, action.kato ?? kato);
            // Кнопку без маршрута не рисуем: нерабочая кнопка хуже её
            // отсутствия.
            if (href !== null) {
              actions.push({ kind: 'navigate', label: action.label, href });
            }
          } else if (action.kind === 'make_note') {
            actions.push({
              kind: 'prompt',
              label: action.label,
              prompt: 'Сделай аналитическую записку по этим данным',
              mode: action.mode ?? 'note',
            });
          }
        }
        break;
      default:
        break;
    }
  }

  void noteId;
  return { text: texts.join('\n\n'), blocks: cards, sources, actions };
};

export interface AskParams {
  message: string;
  /** `null` — пусть бэк определит режим сам (FR-2.2). */
  mode: AssistantMode | null;
  kato: number | null;
  year: number | null;
  conversationId: string | null;
  lang: string;
}

/**
 * Таймаут — свой (180 с вместо общих 10 с клиента): ход диалога — это
 * несколько обращений к LLM подряд (выбор источников → ответ), на stage
 * 28.09 ответы шли 16–54 с. С общими 10 с клиент рвал запрос раньше, чем
 * бэк успевал ответить, и в ленте был «сервис недоступен» при 200 на бэке.
 * Бэк ограничивает каждое обращение к LLM 90 с (`assistant_timeout_seconds`).
 */
export const ASSISTANT_TIMEOUT_MS = 180_000;

/** Ход диалога. */
export const askAssistant = async (
  params: AskParams,
): Promise<AssistantAnswer> => {
  const response = await api.post<ApiChatResponse>(
    '/assistant/chat',
    {
      message: params.message,
      mode: params.mode,
      kato: params.kato,
      year: params.year,
      conversation_id: params.conversationId,
      lang: params.lang,
    },
    { timeout: ASSISTANT_TIMEOUT_MS },
  );
  const data = response.data;
  return {
    conversationId: data.conversation_id ?? null,
    noteId: data.note_id ?? null,
    mode: data.mode ?? null,
    ...normalizeBlocks(data.blocks ?? [], params.kato),
  };
};

/**
 * Режимы, доступные РОЛИ пользователя (FR-2.3).
 *
 * Спрашиваем бэк, а не решаем на фронте: наблюдателю режим «Загрузка» не
 * возвращается вовсе, и это требование постановки (Сценарий 7), а не
 * оформление. Захардкоженный список показал бы ему кнопку, которой он не
 * может воспользоваться.
 */
export const fetchModes = async (
  kato: number | null,
  lang: string,
): Promise<ApiModesResponse> => {
  const response = await api.get<ApiModesResponse>('/assistant/modes', {
    params: { kato: kato ?? undefined, lang },
  });
  return response.data;
};

/** Выгрузка записки. Возвращает файл — конвертацию делает бэк (FR-5.2). */
export const exportNote = async (
  noteId: string,
  format: NoteFormat,
): Promise<Blob> => {
  const response = await api.post<Blob>(
    `/assistant/notes/${encodeURIComponent(noteId)}/export`,
    null,
    { params: { format }, responseType: 'blob', timeout: ASSISTANT_TIMEOUT_MS },
  );
  return response.data;
};
